import {ReactElement} from 'react';
import {
  clearCheckedDocsId,
  clearCodeSameAsDocsId,
  removeCodeSameAsDocs,
  setCheckedDocs,
  setCodeSameAsDocs,
  clearDocumentHistory,
  storeDocumentIdInHistory,
  storeActiveDocumentId,
  triggerCodeSameAs,
  triggerRemoveCodeSameAs,
  storeDocViewConfiguration,
  clearState,
  CodeSameAsToast,
  setCodeSameAsToastState,
  clearCodeSameAsToastState,
  Trigger,
  setActiveDoc,
  setDocViewLoaded,
  setTotalDocsCount,
  setReductionPropagation,
  setCodingFormList,
  setIsPinCodingFormSelected,
  setDocMediaReviewerRemarksPanel,
  setCodingFormToastState,
  clearCodingFormToastState,
  setCompletedDocs,
  removeCompletedDocs,
  clearCompletedDocsId,
  setAssignmentProgressEnabled,
  setWarningToastState,
  clearWarningToastState,
  setNextDoc,
  setDocAnalyticsCurrentTab,
  setDocAnalyticsCheckedDocsThreadMap,
  setDocAnalyticsCheckedDocsFamilyMembers,
  setDocAnalyticsCheckedDocsNearDupe,
  setDocAnalyticsCheckedDocsConceptuallySimilar,
  incrementDocAnalyticsRefreshKey,
} from '../../redux/slice/docViewSlice';
import {store} from '../../redux/store';
import {deleteSignal} from '../PortalPopout/hooks/useSignalStore';
import {CustomMenuItem} from '../common/arr-menu/ArrMenu';
import {
  DocViewKeyword,
  DocViewResponse,
  IDocumentViewerCodingFormListResponse,
} from '../../api/types/document.type';
import {MiniDocRowSelection} from './doc-list/DocViewDocListDataGrid';
import {ResultEntry} from '../document-view-prizmdoc/hooks/usePrizmDoc';
import {
  DocumentListRequestPayload,
  DocumentListSortConfiguration,
  DocumentListSourceDetail,
  DocumentListSourceInfo,
  DocumentListSessionObjects,
  DocumentViewerListIdRequest,
} from '../../api/types/document.type';
import {SourceTypeCode} from '../../shared/constants';
import {generateRandomNumber} from '../../utils/utils';

type MiniDocListRowLookup = (documentId: string) => unknown;

let miniDocListRowLookup: MiniDocListRowLookup | null = null;

export const setMiniDocListRowLookup = (
  lookup: MiniDocListRowLookup | null
): void => {
  miniDocListRowLookup = lookup;
};

export const isDocumentInMiniDocList = (
  documentId?: string | number | null
): boolean => {
  if (documentId == null || documentId === '') return false;
  return miniDocListRowLookup?.(String(documentId)) != null;
};

/** Analytics refresh id: in-list active doc drives refresh; otherwise keep prior context. */
export const getDocAnalyticsDocumentId = (
  activeDocumentId?: string | number | null,
  previouslySelectedDocId?: string
) => {
  if (
    activeDocumentId != null &&
    activeDocumentId !== '' &&
    isDocumentInMiniDocList(activeDocumentId)
  ) {
    return activeDocumentId;
  }
  if (previouslySelectedDocId) {
    return previouslySelectedDocId;
  }
  return activeDocumentId;
};

export const DEFAULT_MIN_PANEL_PCT = 6; // percent used when normalizing heights

export enum MouseToolType {
  Magnifier = 'AccusoftMagnifier',
  SelectToZoom = 'AccusoftSelectToZoom',
  Pan = 'AccusoftPan',
  PanAndEdit = 'AccusoftPanAndEdit',
  SelectText = 'AccusoftSelectText',
  EditMarks = 'AccusoftEditMarks',

  LineAnnotation = 'AccusoftLineAnnotation',
  RectangleAnnotation = 'AccusoftRectangleAnnotation',
  EllipseAnnotation = 'AccusoftEllipseAnnotation',
  TextAnnotation = 'AccusoftTextAnnotation',
  StampAnnotation = 'AccusoftStampAnnotation',
  HighlightAnnotation = 'AccusoftHighlightAnnotation',
  TextHyperlinkAnnotation = 'AccusoftTextHyperlinkAnnotation',
  FreehandAnnotation = 'AccusoftFreehandAnnotation',

  RectangleRedaction = 'AccusoftRectangleRedaction',
  TransparentRectangleRedaction = 'AccusoftTransparentRectangleRedaction',
  TextRedaction = 'AccusoftTextRedaction',
  TextInputSignature = 'AccusoftTextInputSignature',
  TextAreaSignature = 'AccusoftTextAreaSignature',
  StampRedaction = 'AccusoftStampRedaction',
  PlaceSignature = 'AccusoftPlaceSignature',
  TextSelectionRedaction = 'AccusoftTextSelectionRedaction',

  ImageStampAnnotation = 'AccusoftImageStampAnnotation',
  ImageStampRedaction = 'AccusoftImageStampRedaction',

  PolylineAnnotation = 'AccusoftPolylineAnnotation',
  StrikethroughAnnotation = 'AccusoftStrikethroughAnnotation',

  None = 'None',
}

export enum CodeSameAsType {
  add = 'ADD',
  remove = 'REMOVE',
}
export interface Panel {
  id: string;
  title: string;
  component:
    | ReactElement
    | ((props: {
        width: number;
        height: number;
        panelId: string;
      }) => ReactElement);
  menuOptions: CustomMenuItem[];
}

// setting default heights based on number of panels in a section
export const initHeights = (n: number): number[] =>
  n === 0 ? [] : new Array(n).fill(100 / n);

// divide total percentage between panels , take consideration of minumum height
export const normalizeHeights = (
  heights: number[],
  minPanelPercentage = DEFAULT_MIN_PANEL_PCT
) => {
  if (!heights || heights.length === 0) return [];
  const noOfPanels = heights.length;
  if (minPanelPercentage * noOfPanels > 100)
    return new Array(noOfPanels).fill(100 / noOfPanels) as number[];
  const clamped = heights.map((h) =>
    Math.max(h || minPanelPercentage, minPanelPercentage)
  );
  const sum = clamped.reduce((a, b) => a + b, 0);

  return clamped.map((h) => (h / sum) * 100);
};

//-------------------- Redux utility start --------------------
export const setTotalDocumentsCount = (docsCount: number): void => {
  store.dispatch(setTotalDocsCount(docsCount));
};

export const setDocumentViewLoaded = (isLoded: boolean): void => {
  store.dispatch(setDocViewLoaded(isLoded));
};
export const setActiveDocument = (
  activeDoc: MiniDocRowSelection | null
): void => {
  store.dispatch(setActiveDoc(activeDoc));
};
export const setNextDocument = (nextDoc: MiniDocRowSelection | null): void => {
  store.dispatch(setNextDoc(nextDoc));
};
export const setRedactionPropagationAction = (value: boolean): void => {
  store.dispatch(setReductionPropagation(value));
};
export const setDocumentCodeSameAsToastState = (
  codeSameAsToastState: CodeSameAsToast
): void => {
  store.dispatch(setCodeSameAsToastState(codeSameAsToastState));
};
export const clearDocumentCodeSameAsToastState = (): void => {
  store.dispatch(clearCodeSameAsToastState());
};

export const setDocumentCodingFormToastState = (
  codingFormToastState: CodeSameAsToast
): void => {
  store.dispatch(setCodingFormToastState(codingFormToastState));
};
export const clearDocumentCodingFormToastState = (): void => {
  store.dispatch(clearCodingFormToastState());
};
export const setCheckedDocuments = (
  checkedDocs: MiniDocRowSelection[]
): void => {
  store.dispatch(setCheckedDocs(checkedDocs));
};
export const setCodeSameAsDocuments = (
  codeSameAsDocs: MiniDocRowSelection[]
): void => {
  store.dispatch(setCodeSameAsDocs(codeSameAsDocs));
};
export const removeCodeSameAsDocuments = (
  removedCodeSameAsDocs: MiniDocRowSelection[]
): void => {
  store.dispatch(removeCodeSameAsDocs(removedCodeSameAsDocs));
};
export const clearCheckedDocuments = (): void => {
  store.dispatch(clearCheckedDocsId());
};
export const clearCodeSameAsDocs = (): void => {
  store.dispatch(clearCodeSameAsDocsId());
};
export const triggerCodeSameAsRendering = (trigger: Trigger): void => {
  store.dispatch(triggerCodeSameAs(trigger));
};
export const triggerRemoveCodeSameAsRendering = (trigger: Trigger): void => {
  store.dispatch(triggerRemoveCodeSameAs(trigger));
};
export const refreshDocAnalytics = (): void => {
  store.dispatch(incrementDocAnalyticsRefreshKey());
};
export const storeDocIdInHistory = (documentId: MiniDocRowSelection): void => {
  store.dispatch(storeDocumentIdInHistory(documentId));
};

export const storeActiveDocId = (documentId: string): void => {
  store.dispatch(storeActiveDocumentId(documentId));
};
export const getActiveDocId = (): string => {
  const state = store.getState();
  return state.docView.activeDocumentId;
};

export const getAnnotationLayerId = (): any => {
  const state = store.getState();
  return state.docView.docViewConfiguration?.annotationLayerId;
};

export const getDocumentIdsInHistory = (): MiniDocRowSelection[] => {
  const state = store.getState();
  return state.docView.documentHistory;
};

export const clearRecentDocumentHistory = (): void => {
  store.dispatch(clearDocumentHistory());
};
export const clearDocViewState = (): void => {
  setMiniDocListRowLookup(null);
  store.dispatch(clearState());
};
export const storeDocViewConfig = (
  docViewConfiguration: DocViewResponse
): void => {
  store.dispatch(storeDocViewConfiguration(docViewConfiguration));
};

export const storeCodingFormList = (
  codingFormList: IDocumentViewerCodingFormListResponse[]
): void => {
  store.dispatch(setCodingFormList(codingFormList));
};
export const storeSetIsPinCodingFormSelected = (selection: boolean): void => {
  store.dispatch(setIsPinCodingFormSelected(selection));
};

export const resetReviewerRemarksPanel = (): void => {
  store.dispatch(setDocMediaReviewerRemarksPanel(false));
};

export const resetDocAnalyticsState = (): void => {
  deleteSignal('docAnalytics-tabValue');
  store.dispatch(setDocAnalyticsCurrentTab(''));
  store.dispatch(setDocAnalyticsCheckedDocsThreadMap([]));
  store.dispatch(setDocAnalyticsCheckedDocsFamilyMembers([]));
  store.dispatch(setDocAnalyticsCheckedDocsNearDupe([]));
  store.dispatch(setDocAnalyticsCheckedDocsConceptuallySimilar([]));
};

export const defaultHeights: {id: string; height: number}[] = [
  {
    id: 'miniDocList',
    height: 75,
  },
  {
    id: 'docAnalytics',
    height: 25,
  },
  {
    id: 'codingForm',
    height: 75,
  },
  {
    id: 'docDetails',
    height: 25,
  },
];

export const remarksColors = [
  '#29D8E1',
  '#A5C1FF',
  '#D67603',
  '#00AFB8',
  '#A5B4CB',
  '#04A269',
  '#E6C104',
  '#D3E4FF',
  '#D0DB57',
  '#FFC37D',
  '#A5E9D0',
  '#FF9E2C',
  '#E2E997',
  '#FFA3A3',
  '#9F75DF',
  '#F55259',
  '#CCD5E3',
  '#6E87FF',
  '#C4AAEB',
  '#FFEC5B',
  '#A7B22E',
];

export const assignColorsToKeywords = (usedColors: string[], ids: string[]) => {
  const availableColors = remarksColors.filter(
    (color) => !usedColors.includes(color)
  );
  const colorMap: Record<string, string> = {};
  let availableIndex = 0;

  ids.forEach((id) => {
    if (availableIndex < availableColors.length) {
      // assign an unused master color
      colorMap[id] = availableColors[availableIndex];
      availableIndex++;
    } else {
      // fallback: assign a random color from the master list
      const randomColor =
        remarksColors[
          Math.floor(generateRandomNumber() * remarksColors.length)
        ];
      colorMap[id] = randomColor;
    }
  });

  return colorMap;
};

export const searchHighlightColor = '#7AE7EC';

export const getCurrentDate = () => {
  const d = new Date();

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  return `${year}/${month}/${day}`;
};

export const generateTimestampId = (isMultiPageRedaction: boolean) => {
  const now = new Date();

  const year = now.getFullYear(); // 2025
  const month = String(now.getMonth() + 1).padStart(2, '0'); // 11
  const day = String(now.getDate()).padStart(2, '0'); // 17

  const hours = String(now.getHours()).padStart(2, '0'); // 12
  const minutes = String(now.getMinutes()).padStart(2, '0'); // 18
  const seconds = String(now.getSeconds()).padStart(2, '0'); // 41

  const ms = String(now.getMilliseconds()).padStart(3, '0'); // 720
  // Create random last three digit for Multi page redaction because they are created in same ms interval
  const randomMS = Math.floor(generateRandomNumber() * 1000)
    .toString()
    .padStart(3, '0');

  return `${year}${month}${day}${hours}${minutes}${seconds}${isMultiPageRedaction ? randomMS : ms}`;
};

export const baseGetDuplicateDocsPayload = {
  notificationURL: '',
  notificationID: 0,
  fileURL: '',
  documentId: 0,
  masterDocumentId: 0,
  documentNumber: 0,
  documentPath: '',
  documentName: '',
  documentExt: '',
  viewingSessionId: '',
  userAgent: '',
  isSelected: true,
  userIPAddress: '',
  userHostName: '',
  fileType: 'VIEWER',
  seqNo: '',
  contextType: '',
  role: 0,
  securityGroupId: 0,
  projectId: 0,
  userId: 0,
  emailId: '',
  ingestionId: 0,
  pageNumber: 0,
  documentListId: 0,
  highlightedString: '',
  highlightedFlag: true,
  divId: 'string',
  productionId: 0,
  documentFilePaths: [],
  isDocAvailable: true,
  annotationLayerId: 1,
  nearNativeReady: true,
  isPreview: true,
};

export const truncateToFiveDecimals = (num: number) => {
  // Convert to string with enough precision
  const str = num.toString();
  // If there's a decimal point, truncate after 5 places
  if (str.includes('.')) {
    const splitted = str.split('.');
    let decPart = splitted[1];
    decPart = decPart.slice(0, 5);
    return parseFloat(splitted[0] + '.' + decPart);
  }
  // If no decimal part, just return the number
  return num;
};

export const injectScript = (src: string) => {
  return injectHeadResource('script', 'src', src, {async: true});
};

export const injectCss = (href: string) => {
  return injectHeadResource('link', 'href', href, {rel: 'stylesheet'});
};

export const injectHeadResource = (
  tagName: string,
  urlPropertyName: string,
  urlValue: string,
  attributes: {async?: boolean; rel?: string}
) => {
  interface CustomElement extends HTMLElement {
    [key: string]: any;
  }
  return new Promise((resolve, reject) => {
    const tag = document.createElement(tagName) as CustomElement;
    tag[urlPropertyName] = urlValue;

    for (const [k, v] of Object.entries(attributes || {})) {
      tag.setAttribute(k, `${v}`);
    }

    tag.onload = () => {
      tag.onerror = null;
      tag.onload = null;
      resolve(tag);
    };

    tag.onerror = () => {
      tag.onerror = null;
      tag.onload = null;
      reject(new Error(`Failed to load ${urlValue}`));
    };

    document.head.appendChild(tag);
  });
};

//removes line breaks , multiple spaces , hidden characters before comparing
export const normalizeString = (str: string) => {
  return str
    .normalize('NFC')
    .replace(/\r?\n|\r|\u2028|\u2029/g, '')
    .replace(/\s+/g, ' ')
    .replace(/[\u200B-\u200D\uFEFF]/g, '');
};

export const mapKeywordOccurrences = (
  keywords: DocViewKeyword[],
  resultsArray: SearchResult[]
): Record<string, ResultEntry> => {
  const result: Record<string, ResultEntry> = {};

  // Step 1: Collect all keywords + their associated colorCode
  keywords.forEach((item) => {
    const color = item.colorCode ?? '';

    let terms: string[] = [];

    if (item.isConcept && item.keywords) {
      terms = [item.keywords?.trim()];
    } else if (item.keywords) {
      terms = item.keywords
        .split(';')
        .map((t) => t.trim())
        .filter(Boolean);
    }

    terms.forEach((term) => {
      if (!result[term]) {
        result[term] = {
          count: 0,
          colorCode: color,
          hits: [],
          name: item?.isConcept ? (item.description ?? '') : term,
          type: item.isConcept ? 'Concept' : 'Term',
          actualSearchString: term,
        };
      }
    });
  });

  // Step 2: Match target terms with object2 entries
  resultsArray.forEach((entry) => {
    const content = entry.text?.trim();
    if (!content) return;

    // Case-insensitive match (optional: remove .toLowerCase() if not wanted)
    const normalizedContent = normalizeString(content.toLowerCase().trim());

    Object.keys(result).forEach((term) => {
      if (
        normalizeString(term.toLowerCase().trim()) === normalizedContent ||
        (result[term].type === 'Concept' &&
          normalizeString(term.toLowerCase().trim()).includes(
            content
              .toLowerCase()
              .replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
              .replace(/\s+/g, '\\s+')
          ))
      ) {
        result[term].count++;
        result[term].hits.push(entry);
      }
    });
  });

  return result;
};

// Logic taken from Sightline
export const filterAccusoftSearchTerm = (SearchTerm: string) => {
  const finalSearchTermArray: string[] = [];

  if (SearchTerm != '' && SearchTerm != null) {
    const searchTermArray = SearchTerm.split('`');
    searchTermArray.forEach((parameter) => {
      if (
        parameter != '' &&
        parameter.indexOf('AUD:') === -1 &&
        parameter.trim() != 'OR' &&
        parameter.trim() != 'AND' &&
        parameter.trim() != 'NOT'
      ) {
        finalSearchTermArray.push(parameter);
      }
    });
  }
  return finalSearchTermArray;
};

export const createDocumentListSortConfiguration = (
  overrides?: Partial<DocumentListSortConfiguration>
): DocumentListSortConfiguration => ({
  optimizedCols: '',
  optimizedSelectFieldIDs: '',
  optimizedSelectFieldOrder: '',
  manualCols: '',
  manualSelectFieldIDs: '',
  manualSelectFieldOrder: '',
  selectFields: '',
  configuredCols: '',
  order: '',
  showCompletedDocOptimized: false,
  showCompletedDocManual: false,
  configuredShowCompletedDoc: false,
  selectFieldTemp: '',
  selectFieldDataTypeOptimized: [],
  selectFieldDataType: [],
  selectFieldDataTypeManual: [],
  redirectionType: 0,
  manualOrder: '',
  ...overrides,
});

export interface CreateDocumentListRequestPayloadArgs {
  sourceId: number;
  sourceTypeCode: SourceTypeCode;
  docViewSourceTypeCode?: SourceTypeCode;
  sourceDetailOverrides?: Partial<DocumentListSourceDetail>;
  docsToExcludeOverrides?: Partial<DocumentListSourceDetail>;
  sourceInfoOverrides?: Partial<
    Omit<DocumentListSourceInfo, 'sourceDetails' | 'docsToExclude'>
  >;
  docViewSessionOverrides?: Partial<DocumentListSessionObjects>;
  sortConfigurationOverrides?: Partial<DocumentListSortConfiguration>;
}

export const createDocumentListRequestPayload = (
  args: CreateDocumentListRequestPayloadArgs
): DocumentListRequestPayload => {
  const {
    sourceId,
    sourceTypeCode,
    docViewSourceTypeCode,
    sourceInfoOverrides,
    docViewSessionOverrides,
    sortConfigurationOverrides,
  } = args;

  const sortconfiguration = createDocumentListSortConfiguration(
    sortConfigurationOverrides
  );

  const baseSourceInfo: DocumentListSourceInfo = {
    userID: 0,
    securityGroupID: 0,
    roleID: 0,
    projectID: 0,
    sourceDetails: [{sourceID: sourceId}],
    sourceTypeCode,
    assignmentSource: '',
    isMyAssignment: false,
    reviewerID: 0,
    isInverse: false,
    isDefaultLoad: false,
    isAllChildrenSelected: false,
    docsToExclude: [],
    documentListMasterID: 0,
    filters: [],
    sqlUnitParam: [],
    columnFilters: [],
    selectedAssignmentName: '',
    sessionDocViewId: 0,
    destinationTypeCode: '',
    sampleMethod: '',
    uniqueDocumentCount: 0,
    numberToAssign: 0,
    stR_SelectedSearchIds: [],
    destinationContextTypeCode: '',
    isDocumentCount: false,
    totalDocumentToView: -1,
    reportID: '',
    prevPageName: '',
    dataSetStatus: '',
    ...sourceInfoOverrides,
  };

  const baseDocViewSession: DocumentListSessionObjects = {
    sortconfiguration,
    assignmentSortDetails: [],
    documentListMasterID: 0,
    sourceTypeCode: docViewSourceTypeCode ?? sourceTypeCode,
    sourceID: sourceId,
    assignmentSource: '',
    xmlCompletedDocumentEditedCodingForm: '',
    userCodingFormID: 0,
    docsForCodeSame: [],
    docIdsForDocHistory: [],
    backtoSrcDoclistMasterId: 0,
    backtoSrcSourceId: sourceId,
    backtoSrcSourceTypeCode: docViewSourceTypeCode ?? sourceTypeCode,
    ...docViewSessionOverrides,
  };

  return {
    sourceInfo: baseSourceInfo,
    docViewSessionObjects: baseDocViewSession,
  };
};

export interface CreateDocumentViewerListRequestPayloadArgs
  extends CreateDocumentListRequestPayloadArgs {
  sessionDocMasterIds?: number[];
  sessionDocViewHistoryKey?: number[];
  sortConfigurationDocViewOverrides?: Partial<DocumentListSortConfiguration>;
  sortConfigDocViewOverrides?: Partial<DocumentListSortConfiguration>;
  defaultAssName?: string;
}

export const createDocumentViewerListIdRequestPayload = (
  args: CreateDocumentViewerListRequestPayloadArgs
): DocumentViewerListIdRequest => {
  const {
    sessionDocMasterIds = [],
    sessionDocViewHistoryKey = [],
    defaultAssName = '',
    ...rest
  } = args;

  const basePayload = createDocumentListRequestPayload(rest);

  return {
    objSource: basePayload.sourceInfo,
    docViewSessionObject: null,
    sessionDocMasterIds,
    sessionDocViewHistoryKey,
    sortConfigurationDocView: null,
    defaultAssName,
    sortConfigDocView: null,
  };
};

export const setCompletedDocuments = (
  completedDocs: MiniDocRowSelection[]
): void => {
  store.dispatch(setCompletedDocs(completedDocs));
};

export const removeCompletedDocuments = (
  removedCompletedDocs: MiniDocRowSelection[]
): void => {
  store.dispatch(removeCompletedDocs(removedCompletedDocs));
};

export const clearCompletedDocs = (): void => {
  store.dispatch(clearCompletedDocsId());
};

export const setAssignmentProgressEnabledAction = (enabled: boolean): void => {
  store.dispatch(setAssignmentProgressEnabled(enabled));
};

export const setWarningToast = (toast: CodeSameAsToast): void => {
  store.dispatch(setWarningToastState(toast));
};

export const clearWarningToast = (): void => {
  store.dispatch(clearWarningToastState());
};
